import { DatePicker, Select, message } from "antd";
import moment, { Moment } from "moment";
import { useMemo, useState } from "react";
import 'moment/locale/es';

export type PeriodOption =
  | 'ALL'
  | 'CURRENT_MONTH'
  | 'PREVIOUS_MONTH'
  | 'LAST_7DAYS'
  | 'LAST_30DAYS'
  | 'LAST_3MONTHS'
  | 'LAST_12MONTHS'
  | 'CUSTOM_RANGE';

interface SelectPeriodProps {
  setSelectedType: (value: string) => void;
  defaultValue?: string;
  enabledOptions?: PeriodOption[];
  /** Máximo de meses permitidos (rango personalizado, presets largos y meses concretos). Reutilizable por pantalla. */
  maxMonths?: number;
}

const PERIOD_LABELS: Record<PeriodOption, string> = {
  ALL: 'Desde el Inicio',
  CURRENT_MONTH: 'Mes Actual',
  PREVIOUS_MONTH: 'Mes Anterior',
  LAST_7DAYS: 'Últimos 7 días',
  LAST_30DAYS: 'Últimos 30 días',
  LAST_3MONTHS: 'Últimos 3 meses',
  LAST_12MONTHS: 'Últimos 12 meses',
  CUSTOM_RANGE: 'Rango Personalizado'
};

const DEFAULT_OPTIONS = Object.keys(PERIOD_LABELS) as PeriodOption[];

const PRESET_SPAN_MONTHS: Record<PeriodOption, number> = {
  ALL: Number.POSITIVE_INFINITY,
  CURRENT_MONTH: 1,
  PREVIOUS_MONTH: 1,
  LAST_7DAYS: 1,
  LAST_30DAYS: 1,
  LAST_3MONTHS: 3,
  LAST_12MONTHS: 12,
  CUSTOM_RANGE: 0,
};

const MONTH_OPTION_PREFIX = 'MONTH_';

const formatMonthLabel = (date: Moment) => {
  const label = date.locale('es').format('MMMM YYYY');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const toCustomDayRangeValue = (start: Moment, end: Moment) =>
  `CUSTOM_RANGE${start.format('DD-MM-YYYY')},${end.format('DD-MM-YYYY')}`;

const buildPastMonthOptions = (namedPastMonths: number) => {
  // Mes -1 queda como PREVIOUS_MONTH ("Mes Anterior"); aquí meses -2 .. -(1 + namedPastMonths)
  const options: { value: string; label: string; start: Moment; end: Moment }[] = [];
  const lastOffset = 1 + namedPastMonths;
  for (let offset = 2; offset <= lastOffset; offset++) {
    const month = moment().subtract(offset, 'months');
    const start = month.clone().startOf('month');
    const end = month.clone().endOf('month');
    options.push({
      value: `${MONTH_OPTION_PREFIX}${month.format('YYYY-MM')}`,
      label: formatMonthLabel(month),
      start,
      end,
    });
  }
  return options;
};

const filterOptionsByMaxMonths = (options: PeriodOption[], maxMonths?: number) => {
  if (!maxMonths) {
    return options;
  }
  return options.filter((option) => PRESET_SPAN_MONTHS[option] <= maxMonths);
};

export default function SelectPeriod({
  setSelectedType,
  defaultValue = 'ALL',
  enabledOptions,
  maxMonths,
}: SelectPeriodProps) {
  const [selectedOption, setSelectedOption] = useState<string>(defaultValue);
  const [selectedRange, setSelectedRange] = useState<[Moment, Moment] | null>(null);
  const [pickingStart, setPickingStart] = useState<Moment | null>(null);

  const baseOptions = useMemo(
    () => filterOptionsByMaxMonths(enabledOptions || DEFAULT_OPTIONS, maxMonths),
    [enabledOptions, maxMonths]
  );

  const pastMonthOptions = useMemo(
    // Mes Anterior + 3 meses nombrados (independiente del tope de rango)
    () => (maxMonths ? buildPastMonthOptions(3) : []),
    [maxMonths]
  );

  const optionsToRender = useMemo(() => {
    const items: { value: string; label: string; isAll?: boolean }[] = [];

    baseOptions.forEach((optionKey) => {
      items.push({
        value: optionKey,
        label: PERIOD_LABELS[optionKey],
        isAll: optionKey === 'ALL',
      });

      if (optionKey === 'PREVIOUS_MONTH' && pastMonthOptions.length) {
        pastMonthOptions.forEach((monthOption) => {
          items.push({
            value: monthOption.value,
            label: monthOption.label,
          });
        });
      }
    });

    if (pastMonthOptions.length && !baseOptions.includes('PREVIOUS_MONTH')) {
      const customIdx = items.findIndex((item) => item.value === 'CUSTOM_RANGE');
      const insertAt = customIdx >= 0 ? customIdx : items.length;
      items.splice(
        insertAt,
        0,
        ...pastMonthOptions.map((monthOption) => ({
          value: monthOption.value,
          label: monthOption.label,
        }))
      );
    }

    return items;
  }, [baseOptions, pastMonthOptions]);

  const hasAllOption = baseOptions.includes('ALL');
  // Con "Desde el Inicio": activo si no es ALL. Sin ALL (p.ej. maxMonths): siempre activo.
  const isActive = !hasAllOption || selectedOption !== 'ALL';

  const handleChange = (value: string) => {
    setSelectedOption(value);
    setSelectedRange(null);
    setPickingStart(null);

    if (value === 'CUSTOM_RANGE') {
      return;
    }

    if (value.startsWith(MONTH_OPTION_PREFIX)) {
      const monthOption = pastMonthOptions.find((option) => option.value === value);
      if (monthOption) {
        // Primer y último día del mes como rango diario (dd-MM-yyyy)
        setSelectedType(toCustomDayRangeValue(monthOption.start, monthOption.end));
      }
      return;
    }

    setSelectedType(value);
  };

  const isRangeTooLong = (start: Moment, end: Moment) => {
    if (!maxMonths) {
      return false;
    }
    return end.diff(start, 'months', true) > maxMonths;
  };

  const handleRangeChange = (dates: any) => {
    if (!dates || dates.length < 2 || !dates[0] || !dates[1]) {
      setSelectedRange(null);
      return;
    }

    const start = moment(dates[0]);
    const end = moment(dates[1]);

    if (!start.isValid() || !end.isValid()) {
      setSelectedRange(null);
      setPickingStart(null);
      return;
    }

    if (maxMonths && isRangeTooLong(start, end)) {
      message.warning(`El rango no puede superar ${maxMonths} meses`);
      setSelectedRange(null);
      setPickingStart(null);
      return;
    }

    setSelectedRange([start, end]);
    setPickingStart(null);
    setSelectedOption('CUSTOM_RANGE');
    // Backend: FORMATTER = dd-MM-yyyy
    setSelectedType(toCustomDayRangeValue(start, end));
  };

  const disabledDate = (current: Moment) => {
    if (!current || !maxMonths || !pickingStart) {
      return false;
    }

    // Solo limita la distancia entre start y end; se puede elegir cualquier año
    const earliest = pickingStart.clone().subtract(maxMonths, 'months');
    const latest = pickingStart.clone().add(maxMonths, 'months');
    return current.isBefore(earliest, 'day') || current.isAfter(latest, 'day');
  };

  return (
    <div
      className={isActive ? 'page-filter-control--active' : undefined}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <Select
        bordered={false}
        onChange={handleChange}
        value={selectedOption}
        className={isActive ? 'page-filter-control--active' : undefined}
        style={{
          minWidth: '180px',
          color: isActive ? '#1b6ef5' : 'royalblue',
          fontWeight: 'bold',
          fontSize: '16px',
          textAlign: 'left',
        }}
      >
        {optionsToRender.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            <span style={{ color: option.isAll ? 'black' : 'inherit' }}>
              {option.label}
            </span>
          </Select.Option>
        ))}
      </Select>

      {selectedOption === 'CUSTOM_RANGE' && (
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          size="small"
          style={{ marginLeft: 10 }}
          onChange={handleRangeChange}
          onCalendarChange={(dates: any) => {
            setPickingStart(dates?.[0] && !dates?.[1] ? dates[0] : null);
          }}
          value={selectedRange as any}
          disabledDate={maxMonths ? disabledDate : undefined}
        />
      )}
    </div>
  );
}
